import {
  MASTER_TRACK_ID,
  MAX_TRACK_HEIGHT,
  MIN_TRACK_HEIGHT,
  TRACK_COLORS,
  createInstrument,
  createTrack,
} from '@/domain/project';
import { defaultInstrumentParams } from '@/engine/instruments/descriptors';
import { removeClips, withTrack, withTracks } from '@/domain/mutate';
import { wouldCreateRoutingCycle } from '@/domain/selectors';
import type { AutomationMode, Project, Send, TrackKind } from '@/domain/types';
import { newSendId, newTrackId } from '@/util/id';
import { defineCommand } from './registry';
import { boolParam, enumParam, idParam, intParam, numberParam, stringParam } from './params';

const TRACK_KINDS: readonly TrackKind[] = ['audio', 'instrument', 'bus', 'aux'];

export const addTrack = defineCommand<{ kind: TrackKind; name: string | null; trackId: string; index: number | null }>({
  type: 'track.add',
  label: (p) => `Add ${p.kind} track`,
  parse: (raw) => {
    const kind = enumParam(raw.kind, TRACK_KINDS, 'audio');
    const trackId = raw.trackId === undefined ? newTrackId() : idParam(raw.trackId);
    if (!kind || !trackId) return null;
    return {
      kind,
      name: stringParam(raw.name, 128) ?? null,
      trackId,
      index: raw.index === undefined ? null : intParam(raw.index, 0, 10000),
    };
  },
  apply: (project, p) => {
    if (project.tracks[p.trackId]) return project;
    const position = p.index === null ? project.trackOrder.length : Math.min(p.index, project.trackOrder.length);
    const defaultName =
      p.name ??
      `${p.kind === 'audio' ? 'Audio' : p.kind === 'instrument' ? 'Inst' : p.kind === 'bus' ? 'Bus' : 'Return'} ${
        project.trackOrder.filter((id) => project.tracks[id]?.kind === p.kind).length + 1
      }`;
    const track = createTrack(p.kind, defaultName, project.trackOrder.length, p.trackId);
    track.color = TRACK_COLORS[project.trackOrder.length % TRACK_COLORS.length];
    track.outputTrackId = project.masterTrackId;
    // An instrument track with no instrument is silent and confusing; it opens
    // with a synth loaded, which is what "add an instrument track" means.
    if (p.kind === 'instrument') {
      track.instrument = createInstrument('synth', defaultInstrumentParams('synth'));
    }
    const trackOrder = [...project.trackOrder];
    trackOrder.splice(position, 0, track.id);
    return { ...project, tracks: { ...project.tracks, [track.id]: track }, trackOrder };
  },
});

export const removeTrack = defineCommand<{ trackId: string }>({
  type: 'track.remove',
  label: (p, project) => `Delete track "${project.tracks[p.trackId]?.name ?? ''}"`,
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    return trackId ? { trackId } : null;
  },
  apply: (project, p) => {
    const track = project.tracks[p.trackId];
    if (!track || track.kind === 'master') return project;

    let next = removeClips(project, track.clipIds);
    const tracks = { ...next.tracks };
    delete tracks[p.trackId];

    // Anything routed into the deleted track falls back to master, and sends
    // that pointed at it are dropped. A deleted bus must never silence a track.
    for (const other of Object.values(tracks)) {
      const needsReroute = other.outputTrackId === p.trackId;
      const sends = other.sends.filter((s) => s.targetTrackId !== p.trackId);
      if (needsReroute || sends.length !== other.sends.length) {
        tracks[other.id] = {
          ...other,
          outputTrackId: needsReroute ? next.masterTrackId : other.outputTrackId,
          sends,
        };
      }
    }

    const automation = { ...next.automation };
    for (const lane of Object.values(automation)) {
      if (lane.target.trackId === p.trackId) delete automation[lane.id];
    }

    next = {
      ...next,
      tracks,
      automation,
      trackOrder: next.trackOrder.filter((id) => id !== p.trackId),
    };
    return next;
  },
});

export const renameTrack = defineCommand<{ trackId: string; name: string }>({
  type: 'track.rename',
  label: (p) => `Rename track to "${p.name}"`,
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const name = stringParam(raw.name, 128);
    return trackId && name ? { trackId, name } : null;
  },
  coalesceKey: (p) => `rename:${p.trackId}`,
  apply: (project, p) => withTrack(project, p.trackId, (t) => (t.name === p.name ? t : { ...t, name: p.name })),
});

export const setTrackColor = defineCommand<{ trackId: string; color: string }>({
  type: 'track.setColor',
  label: () => 'Change track colour',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const color = typeof raw.color === 'string' && /^#[0-9a-fA-F]{6}$/.test(raw.color) ? raw.color : null;
    return trackId && color ? { trackId, color } : null;
  },
  apply: (project, p) => withTrack(project, p.trackId, (t) => ({ ...t, color: p.color })),
});

export const setTrackHeight = defineCommand<{ trackId: string; height: number }>({
  type: 'track.setHeight',
  label: () => 'Resize track',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const height = intParam(raw.height, MIN_TRACK_HEIGHT, MAX_TRACK_HEIGHT);
    return trackId && height !== null ? { trackId, height } : null;
  },
  coalesceKey: (p) => `height:${p.trackId}`,
  apply: (project, p) => withTrack(project, p.trackId, (t) => ({ ...t, height: p.height })),
});

export const setAutomationMode = defineCommand<{ trackId: string; mode: AutomationMode }>({
  type: 'track.setAutomationMode',
  label: (p, project) => `Set "${project.tracks[p.trackId]?.name ?? 'track'}" to ${p.mode}`,
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const mode = enumParam<AutomationMode>(raw.mode, ['read', 'touch', 'latch', 'write']);
    return trackId && mode ? { trackId, mode } : null;
  },
  apply: (project, p) =>
    withTrack(project, p.trackId, (t) => (t.automationMode === p.mode ? t : { ...t, automationMode: p.mode })),
});

export const setTrackVolume = defineCommand<{ trackId: string; volume: number }>({
  type: 'track.setVolume',
  label: (p, project) => `Set "${project.tracks[p.trackId]?.name ?? 'track'}" volume`,
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const volume = numberParam(raw.volume, 0, 4);
    return trackId && volume !== null ? { trackId, volume } : null;
  },
  coalesceKey: (p) => `volume:${p.trackId}`,
  apply: (project, p) => withTrack(project, p.trackId, (t) => (t.volume === p.volume ? t : { ...t, volume: p.volume })),
});

export const setTrackPan = defineCommand<{ trackId: string; pan: number }>({
  type: 'track.setPan',
  label: (p, project) => `Set "${project.tracks[p.trackId]?.name ?? 'track'}" pan`,
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const pan = numberParam(raw.pan, -1, 1);
    return trackId && pan !== null ? { trackId, pan } : null;
  },
  coalesceKey: (p) => `pan:${p.trackId}`,
  apply: (project, p) => withTrack(project, p.trackId, (t) => (t.pan === p.pan ? t : { ...t, pan: p.pan })),
});

export const setTrackMute = defineCommand<{ trackId: string; mute: boolean }>({
  type: 'track.setMute',
  label: (p) => (p.mute ? 'Mute track' : 'Unmute track'),
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const mute = boolParam(raw.mute);
    return trackId && mute !== null ? { trackId, mute } : null;
  },
  apply: (project, p) => withTrack(project, p.trackId, (t) => ({ ...t, mute: p.mute })),
});

export const setTrackSolo = defineCommand<{ trackId: string; solo: boolean; exclusive: boolean }>({
  type: 'track.setSolo',
  label: (p) => (p.solo ? 'Solo track' : 'Unsolo track'),
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const solo = boolParam(raw.solo);
    return trackId && solo !== null ? { trackId, solo, exclusive: boolParam(raw.exclusive, false) ?? false } : null;
  },
  apply: (project, p) => {
    if (!p.exclusive) return withTrack(project, p.trackId, (t) => ({ ...t, solo: p.solo }));
    return withTracks(project, Object.keys(project.tracks), (t) => {
      const solo = t.id === p.trackId ? p.solo : false;
      return t.solo === solo ? t : { ...t, solo };
    });
  },
});

export const setTrackArm = defineCommand<{ trackId: string; armed: boolean }>({
  type: 'track.setArm',
  label: (p) => (p.armed ? 'Arm track for recording' : 'Disarm track'),
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const armed = boolParam(raw.armed);
    return trackId && armed !== null ? { trackId, armed } : null;
  },
  apply: (project, p) =>
    withTrack(project, p.trackId, (t) => (t.kind === 'audio' || t.kind === 'instrument' ? { ...t, armed: p.armed } : t)),
});

export const setTrackMonitor = defineCommand<{ trackId: string; monitor: boolean }>({
  type: 'track.setMonitor',
  label: () => 'Toggle input monitoring',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const monitor = boolParam(raw.monitor);
    return trackId && monitor !== null ? { trackId, monitor } : null;
  },
  apply: (project, p) => withTrack(project, p.trackId, (t) => ({ ...t, inputMonitor: p.monitor })),
});

export const setTrackInputDevice = defineCommand<{ trackId: string; deviceId: string | null }>({
  type: 'track.setInputDevice',
  label: () => 'Change track input',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    if (!trackId) return null;
    const deviceId = raw.deviceId === null ? null : stringParam(raw.deviceId, 256);
    return { trackId, deviceId };
  },
  apply: (project, p) => withTrack(project, p.trackId, (t) => ({ ...t, inputDeviceId: p.deviceId })),
});

export const setTrackOutput = defineCommand<{ trackId: string; outputTrackId: string }>({
  type: 'track.setOutput',
  label: () => 'Change track routing',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const outputTrackId = idParam(raw.outputTrackId);
    return trackId && outputTrackId ? { trackId, outputTrackId } : null;
  },
  apply: (project, p) => {
    const track = project.tracks[p.trackId];
    const target = project.tracks[p.outputTrackId];
    if (!track || !target || track.kind === 'master') return project;
    // Feedback loops are silently refused rather than allowed and then muted:
    // a routing UI that accepts an impossible patch is worse than one that
    // declines it.
    if (wouldCreateRoutingCycle(project, p.trackId, p.outputTrackId)) return project;
    return withTrack(project, p.trackId, (t) => ({ ...t, outputTrackId: p.outputTrackId }));
  },
});

export const reorderTracks = defineCommand<{ trackId: string; toIndex: number }>({
  type: 'track.reorder',
  label: () => 'Reorder tracks',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const toIndex = intParam(raw.toIndex, 0, 10000);
    return trackId && toIndex !== null ? { trackId, toIndex } : null;
  },
  apply: (project, p) => {
    const from = project.trackOrder.indexOf(p.trackId);
    if (from < 0) return project;
    const trackOrder = [...project.trackOrder];
    trackOrder.splice(from, 1);
    trackOrder.splice(Math.min(p.toIndex, trackOrder.length), 0, p.trackId);
    return { ...project, trackOrder };
  },
});

export const addSend = defineCommand<{ trackId: string; targetTrackId: string; sendId: string; gain: number }>({
  type: 'track.addSend',
  label: () => 'Add send',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const targetTrackId = idParam(raw.targetTrackId);
    const sendId = raw.sendId === undefined ? newSendId() : idParam(raw.sendId);
    const gain = numberParam(raw.gain, 0, 4, 0.5);
    if (!trackId || !targetTrackId || !sendId || gain === null) return null;
    return { trackId, targetTrackId, sendId, gain };
  },
  apply: (project, p) => {
    const track = project.tracks[p.trackId];
    const target = project.tracks[p.targetTrackId];
    if (!track || !target || track.id === target.id) return project;
    if (track.sends.some((s) => s.targetTrackId === p.targetTrackId)) return project;
    if (wouldCreateRoutingCycle(project, p.trackId, p.targetTrackId)) return project;
    const send: Send = { id: p.sendId, targetTrackId: p.targetTrackId, gain: p.gain, preFader: false, enabled: true };
    return withTrack(project, p.trackId, (t) => ({ ...t, sends: [...t.sends, send] }));
  },
});

export const setSendGain = defineCommand<{ trackId: string; sendId: string; gain: number }>({
  type: 'track.setSendGain',
  label: () => 'Set send level',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const sendId = idParam(raw.sendId);
    const gain = numberParam(raw.gain, 0, 4);
    return trackId && sendId && gain !== null ? { trackId, sendId, gain } : null;
  },
  coalesceKey: (p) => `send:${p.trackId}:${p.sendId}`,
  apply: (project, p) =>
    withTrack(project, p.trackId, (t) => ({
      ...t,
      sends: t.sends.map((s) => (s.id === p.sendId ? { ...s, gain: p.gain } : s)),
    })),
});

/**
 * Whether a send is taken before the fader or after it.
 *
 * "Pre-fader" here means what it means on a console: after the inserts and
 * before the fader, so pulling the fader down leaves the send — a headphone
 * mix, a reverb that should not follow a fade — where it was. It is not
 * pre-insert; there is no tap before the inserts, on a live track or a frozen
 * one, which is what lets a frozen track's sends carry exactly what the live
 * track's did.
 */
export const setSendPreFader = defineCommand<{ trackId: string; sendId: string; preFader: boolean }>({
  type: 'track.setSendPreFader',
  label: (p) => (p.preFader ? 'Send pre-fader' : 'Send post-fader'),
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const sendId = idParam(raw.sendId);
    const preFader = boolParam(raw.preFader);
    return trackId && sendId && preFader !== null ? { trackId, sendId, preFader } : null;
  },
  apply: (project, p) =>
    withTrack(project, p.trackId, (t) => ({
      ...t,
      sends: t.sends.map((s) => (s.id === p.sendId ? { ...s, preFader: p.preFader } : s)),
    })),
});

export const setSendEnabled = defineCommand<{ trackId: string; sendId: string; enabled: boolean }>({
  type: 'track.setSendEnabled',
  label: (p) => (p.enabled ? 'Enable send' : 'Mute send'),
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const sendId = idParam(raw.sendId);
    const enabled = boolParam(raw.enabled);
    return trackId && sendId && enabled !== null ? { trackId, sendId, enabled } : null;
  },
  apply: (project, p) =>
    withTrack(project, p.trackId, (t) => ({
      ...t,
      sends: t.sends.map((s) => (s.id === p.sendId ? { ...s, enabled: p.enabled } : s)),
    })),
});

export const removeSend = defineCommand<{ trackId: string; sendId: string }>({
  type: 'track.removeSend',
  label: () => 'Remove send',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const sendId = idParam(raw.sendId);
    return trackId && sendId ? { trackId, sendId } : null;
  },
  apply: (project, p) =>
    withTrack(project, p.trackId, (t) => ({ ...t, sends: t.sends.filter((s) => s.id !== p.sendId) })),
});

export const clearAllSolo = defineCommand<Record<string, never>>({
  type: 'track.clearSolo',
  label: () => 'Clear all solos',
  parse: () => ({}) as Record<string, never>,
  apply: (project: Project) =>
    withTracks(project, Object.keys(project.tracks), (t) => (t.solo ? { ...t, solo: false } : t)),
});

export const MASTER_ID = MASTER_TRACK_ID;
